import { useState, useCallback, useEffect } from "react";
import { format } from "date-fns";
import { AlarmClock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog as DialogBase,
  DialogContent as DialogContentBase,
} from "@/components/ui/dialog";
import {
  Dialog as DialogStacked,
  DialogContent as DialogContentStacked,
} from "@/components/ui/dialog-stacked";
import { ModalHeader } from "@/components/ui/modal-header";
import { ModalHeaderStacked } from "@/components/ui/modal-header-stacked";
import type { NotificationUpcomingItem } from "@/services/notifications.service";

interface ReminderFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  anchorTime?: Date | null;
  onSave: (fireAtMs: number, note?: string) => Promise<void>;
  onSaved: () => void;
  editing?: NotificationUpcomingItem | null;
  /** Use dialog-stacked when layered on top of another modal */
  stacked?: boolean;
  /** Custom label for the primary action button (default: "Save") */
  actionLabel?: string;
  /** When provided, shows a "Back" button alongside the primary action */
  onBack?: () => void;
}

export function ReminderFormModal({
  open,
  onOpenChange,
  anchorTime,
  onSave,
  onSaved,
  editing = null,
  stacked = false,
  actionLabel,
  onBack,
}: ReminderFormModalProps) {
  const canUseRelative = Boolean(anchorTime);
  const [reminderMode, setReminderMode] = useState<"in" | "before">("in");
  const [selectedPreset, setSelectedPreset] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");
  const [customTime, setCustomTime] = useState("");

  // Reset form state when modal opens
  useEffect(() => {
    if (open) {
      setSaving(false);
      setReminderMode("in");

      if (editing?.fire_at_ms) {
        const ms = Number(editing.fire_at_ms);
        if (Number.isFinite(ms)) {
          setSelectedPreset("");
          setCustomTime(format(new Date(ms), "yyyy-MM-dd'T'HH:mm"));
        } else {
          setSelectedPreset("");
          setCustomTime(format(new Date(Date.now() + 15 * 60_000), "yyyy-MM-dd'T'HH:mm"));
        }
      } else {
        setSelectedPreset("");
        setCustomTime(format(new Date(Date.now() + 15 * 60_000), "yyyy-MM-dd'T'HH:mm"));
      }

      setNote(editing?.payload?.body || "");
    }
  }, [open, editing]);

  const handlePresetChange = useCallback(
    (value: string) => {
      setSelectedPreset(value);
      const minutes = Number(value);
      if (minutes > 0) {
        const ms =
          reminderMode === "before" && anchorTime
            ? anchorTime.getTime() - minutes * 60_000
            : Date.now() + minutes * 60_000;
        if (!Number.isFinite(ms)) return;
        setCustomTime(format(new Date(ms), "yyyy-MM-dd'T'HH:mm"));
      }
    },
    [reminderMode, anchorTime],
  );

  const handleModeChange = useCallback((mode: "in" | "before") => {
    setReminderMode(mode);
    if (mode === "before" && anchorTime) {
      setSelectedPreset("1440");
      const ms = anchorTime.getTime() - 1440 * 60_000;
      if (Number.isFinite(ms)) {
        setCustomTime(format(new Date(ms), "yyyy-MM-dd'T'HH:mm"));
      }
    } else {
      setSelectedPreset("");
    }
  }, [anchorTime]);

  const handleDateTimeChange = useCallback((value: string) => {
    setCustomTime(value);
    setSelectedPreset("");
  }, []);

  const fireAtMs = (() => {
    if (!customTime) return null;
    const parsed = new Date(customTime);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.getTime();
  })();

  const isValidTime = fireAtMs !== null && fireAtMs > Date.now() + 5000;

  const handleSave = async () => {
    if (!fireAtMs || !isValidTime) return;
    setSaving(true);
    try {
      await onSave(fireAtMs, note.trim() || undefined);
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  const DialogRoot = stacked ? DialogStacked : DialogBase;
  const DialogContentComp = stacked ? DialogContentStacked : DialogContentBase;
  const HeaderComp = stacked ? ModalHeaderStacked : ModalHeader;

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContentComp
        className="max-w-[90vw] sm:max-w-md"
        onFocusOutside={(e) => e.preventDefault()}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <HeaderComp
          icon={<AlarmClock />}
          title={editing ? "Edit Reminder" : "New Reminder"}
          description="Set a time and optionally add a note."
        />

        <div className="grid gap-4 pt-2 pb-4">
          {/* Mode + Preset row */}
          <div className="flex gap-2">
            <select
              value={reminderMode}
              onChange={(e) =>
                handleModeChange(e.target.value as "in" | "before")
              }
              disabled={saving}
              className="text-ui-field w-[140px] h-9 rounded-lg border border-border bg-background px-3 text-foreground appearance-none cursor-pointer disabled:opacity-50"
            >
              <option value="in">Remind me in</option>
              {canUseRelative && <option value="before">Before event</option>}
            </select>
            <select
              value={selectedPreset}
              onChange={(e) => handlePresetChange(e.target.value)}
              disabled={saving}
              className="text-ui-field flex-1 h-9 rounded-lg border border-border bg-background px-3 text-foreground appearance-none cursor-pointer disabled:opacity-50"
            >
              <option value="">Select...</option>
              <option value="5">5 min</option>
              <option value="15">15 min</option>
              <option value="30">30 min</option>
              <option value="60">1 hour</option>
              <option value="1440">1 day</option>
            </select>
          </div>

          {/* Datetime picker */}
          <div className="overflow-hidden rounded-lg border border-border">
            <input
              type="datetime-local"
              value={customTime}
              onChange={(e) => handleDateTimeChange(e.target.value)}
              disabled={saving}
              className="text-ui-field w-full h-9 bg-background px-3 text-foreground disabled:opacity-50 border-0 dark:[color-scheme:dark]"
            />
          </div>

          {/* Divider */}
          <div className="border-t border-border" />

          {/* Note */}
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Write a note..."
            disabled={saving}
            rows={3}
            className="text-ui-field w-full rounded-lg border border-border bg-background px-3 py-2.5 text-foreground placeholder:text-muted-foreground resize-none disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
          />

          {/* Actions */}
          <div className="flex gap-3">
            {onBack && (
              <Button
                onClick={onBack}
                disabled={saving}
                variant="secondary"
                size="modal"
                className="flex-1"
              >
                Back
              </Button>
            )}
            <Button
              onClick={handleSave}
              disabled={saving || !isValidTime}
              variant="gradient"
              size="modal"
              className={onBack ? "flex-1" : "w-full"}
            >
              {saving
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : actionLabel ?? (editing ? "Save Changes" : "Save")}
            </Button>
          </div>
        </div>
      </DialogContentComp>
    </DialogRoot>
  );
}
